import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Plus, Trash2, Eye, Smartphone, Monitor, UploadCloud, Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, mailingApi } from "./api";

const MAX_UI_ATTACHMENT_BYTES = 5 * 1024 * 1024;

export default function CampaignEditor({ value, onChange, allowAttachments = true }) {
  const v = useMemo(() => value || {}, [value]);
  const set = (patch) => onChange({ ...v, ...patch });
  const setBullet = (idx, patch) => {
    const bullets = [...(v.bullets || [])];
    bullets[idx] = { ...bullets[idx], ...patch };
    set({ bullets });
  };
  const addBullet = () => set({ bullets: [...(v.bullets || []), { title: "", body: "" }] });
  const removeBullet = (idx) => set({ bullets: (v.bullets || []).filter((_, i) => i !== idx) });

  const [previewMode, setPreviewMode] = useState("mobile"); // mobile | desktop
  const [previewHtml, setPreviewHtml] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const uploadHeroImage = async (file) => {
    if (!file) return;
    if (file.size > MAX_UI_ATTACHMENT_BYTES) {
      toast.error(`${file.name} is larger than 5 MB`);
      return;
    }
    setUploadingImage(true);
    try {
      const out = await mailingApi.uploads.image(file);
      set({ hero_image_url: out.url });
      toast.success("Header image uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Image upload failed"));
    } finally {
      setUploadingImage(false);
    }
  };

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const out = await mailingApi.templates.preview(v);
      setPreviewHtml(out.html);
    } catch {
      setPreviewHtml("<p style='padding:24px;color:#a55'>Preview failed</p>");
    } finally {
      setLoadingPreview(false);
    }
  }, [v]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 600);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const uploadAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const tooLarge = files.find((file) => file.size > MAX_UI_ATTACHMENT_BYTES);
    if (tooLarge) {
      toast.error(`${tooLarge.name} is larger than 5 MB`);
      return;
    }

    setUploadingAttachment(true);
    try {
      const uploaded = [];
      for (const file of files) {
        uploaded.push(await mailingApi.uploads.attachment(file, v.id || "draft"));
      }
      set({ attachments: [...(v.attachments || []), ...uploaded] });
      toast.success(uploaded.length === 1 ? "Attachment added" : `${uploaded.length} attachments added`);
    } catch (err) {
      toast.error(formatApiError(err, "Attachment upload failed"));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = (index) => {
    set({ attachments: (v.attachments || []).filter((_, i) => i !== index) });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Editor */}
      <div className="space-y-5">
        <div className="mailing-design-card">
          <div>
            <div className="velora-micro text-[#7B3F00]">Premium template system</div>
            <div className="mailing-design-title">A polished export note, built for inboxes.</div>
          </div>
          <p>
            The live email now uses a branded ivory-and-indigo layout, proof strip,
            stronger call-to-action, clean plain-text fallback, and attachment-safe delivery.
          </p>
        </div>

        <Field label="Subject *" value={v.subject || ""} onChange={(x) => set({ subject: x })} hint="Use {{name}}, {{company}} for personalization" />
        <Field label="Preheader" value={v.preheader || ""} onChange={(x) => set({ preheader: x })} hint="Shown in inbox preview, ~80 chars" />
        <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-4 space-y-4">
          <div className="velora-micro text-[#7B3F00]">Hero</div>
          <Field label="Headline" value={v.hero_headline || ""} onChange={(x) => set({ hero_headline: x })} />
          <Field label="Sub-headline" value={v.hero_subheadline || ""} onChange={(x) => set({ hero_subheadline: x })} />

          {/* Header image: upload (hosted publicly so email clients can load it) or paste a URL */}
          <div>
            <label className="velora-micro text-[#3D1F00]/50 block mb-1">Header image (optional)</label>
            {v.hero_image_url ? (
              <div className="mb-2 border border-[#E0D4B6] bg-[#FAF3E0] p-2 flex items-center gap-3">
                <img src={v.hero_image_url} alt="Header preview" className="w-20 h-14 object-cover border border-[#E0D4B6]" />
                <span className="velora-micro text-[#3D1F00]/50 flex-1 truncate" title={v.hero_image_url}>{v.hero_image_url}</span>
                <button type="button" onClick={() => set({ hero_image_url: "" })} className="text-[#3D1F00]/40 hover:text-[#7B3F00]" title="Remove header image">
                  <X size={14} />
                </button>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={v.hero_image_url || ""}
                onChange={(e) => set({ hero_image_url: e.target.value })}
                placeholder="Paste a public image URL, or upload →"
                className="velora-input flex-1"
              />
              <label className={`mailing-upload-button shrink-0 ${uploadingImage ? "is-disabled" : ""}`}>
                <ImageIcon size={14} />
                <span>{uploadingImage ? "Uploading..." : "Upload"}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingImage}
                  onChange={(e) => { uploadHeroImage(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
            </div>
            <div className="velora-micro text-[#3D1F00]/30 mt-1">Uploaded images are hosted on a public link so they show in Gmail/Outlook. Recommended width 1200px, under 5 MB.</div>
          </div>
        </div>
        <Textarea label="Intro paragraph" value={v.intro_paragraph || ""} onChange={(x) => set({ intro_paragraph: x })} rows={4} />

        <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="velora-micro text-[#7B3F00]">Bullets ({v.bullets?.length || 0})</div>
            <button onClick={addBullet} type="button" className="velora-btn-ghost flex items-center gap-1 !text-[#7B3F00]">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(v.bullets || []).map((b, idx) => (
              <div key={idx} className="flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <input
                    placeholder="Title"
                    className="velora-input col-span-1"
                    value={b.title || ""}
                    onChange={(e) => setBullet(idx, { title: e.target.value })}
                  />
                  <input
                    placeholder="Body"
                    className="velora-input col-span-2"
                    value={b.body || ""}
                    onChange={(e) => setBullet(idx, { body: e.target.value })}
                  />
                </div>
                <button onClick={() => removeBullet(idx)} type="button" className="text-[#3D1F00]/30 hover:text-[#7B3F00] mt-2">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA label" value={v.cta_label || ""} onChange={(x) => set({ cta_label: x })} />
          <Field label="CTA URL" value={v.cta_url || ""} onChange={(x) => set({ cta_url: x })} />
        </div>

        {allowAttachments && (
          <AttachmentsPanel
            attachments={v.attachments || []}
            uploading={uploadingAttachment}
            onUpload={uploadAttachments}
            onRemove={removeAttachment}
          />
        )}
      </div>

      {/* Preview */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="velora-micro text-[#7B3F00] flex items-center gap-2"><Eye size={12} /> Preview</div>
          <div className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={() => setPreviewMode("mobile")}
              className={`p-2 border ${previewMode === "mobile" ? "border-[#E8943A] text-[#7B3F00]" : "border-[#E0D4B6] text-[#3D1F00]/40"}`}
              title="Mobile"
            >
              <Smartphone size={12} />
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode("desktop")}
              className={`p-2 border ${previewMode === "desktop" ? "border-[#E8943A] text-[#7B3F00]" : "border-[#E0D4B6] text-[#3D1F00]/40"}`}
              title="Desktop"
            >
              <Monitor size={12} />
            </button>
          </div>
        </div>
        <div className="border border-[#E0D4B6] bg-[#FAF3E0] p-3 flex items-center justify-center min-h-[600px] sticky top-6">
          <iframe
            title="email preview"
            srcDoc={previewHtml || "<p style='padding:24px;font-family:sans-serif;color:#888'>Loading…</p>"}
            sandbox=""
            className="bg-white"
            style={{
              width: previewMode === "mobile" ? "375px" : "100%",
              height: "640px",
              maxWidth: "100%",
              border: "1px solid #ddd",
            }}
          />
        </div>
        {loadingPreview && <div className="velora-micro text-[#3D1F00]/40 mt-2">Updating preview…</div>}
      </div>
    </div>
  );
}

function AttachmentsPanel({ attachments, uploading, onUpload, onRemove }) {
  const inputId = "campaign-attachments";

  return (
    <div className="mailing-attachments-panel">
      <div className="mailing-attachments-head">
        <div>
          <div className="velora-micro text-[#7B3F00]">Attachments</div>
          <div className="mailing-attachments-title">Files sent with the email</div>
        </div>
        <label htmlFor={inputId} className={`mailing-upload-button ${uploading ? "is-disabled" : ""}`}>
          <UploadCloud size={15} />
          <span>{uploading ? "Uploading..." : "Add files"}</span>
        </label>
        <input
          id={inputId}
          type="file"
          multiple
          className="hidden"
          disabled={uploading}
          onChange={(event) => {
            onUpload(event.target.files);
            event.target.value = "";
          }}
        />
      </div>

      <div className="mailing-attachments-drop">
        <Paperclip size={16} />
        <span>PDFs, catalogues, price sheets, specs, and product photos up to 5 MB each.</span>
      </div>

      {attachments.length === 0 ? (
        <div className="mailing-empty-attachments">
          <FileText size={18} />
          <span>No attachments added yet.</span>
        </div>
      ) : (
        <div className="mailing-attachment-list">
          {attachments.map((att, index) => (
            <div key={`${att.storage_path || att.filename}-${index}`} className="mailing-attachment-row">
              <span className="mailing-attachment-icon"><FileText size={15} /></span>
              <span className="mailing-attachment-name" title={att.filename}>{att.filename}</span>
              <span className="mailing-attachment-meta">{formatBytes(att.size)} · attached</span>
              <button type="button" onClick={() => onRemove(index)} aria-label={`Remove ${att.filename}`}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes = 0) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Field({ label, value, onChange, hint, type = "text" }) {
  return (
    <div>
      <label className="velora-micro text-[#3D1F00]/50 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="velora-input w-full" />
      {hint && <div className="velora-micro text-[#3D1F00]/30 mt-1">{hint}</div>}
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="velora-micro text-[#3D1F00]/50 block mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="velora-input w-full font-sans"
      />
    </div>
  );
}
